/**
 * GH#1763 [TENANT FIRST] (2026-09-29, TINA-RDIINFRA, letra TINA-RDICTO):
 * `runtimeCaching` del service worker, sacado de vite.config.ts a un módulo
 * puro para que un test pueda afirmar sobre la config EXACTA que usa el build.
 *
 * Qué cambió y por qué:
 *
 * 1. Se ELIMINÓ la regla `api-cache` (NetworkFirst sobre `/api/`, 10 min).
 *    Guardaba respuestas de la API con datos del tenant (propiedades,
 *    agentes, notificaciones) con clave SOLO por URL -- sin el token -- y no
 *    se borraba al cerrar sesión (medido en prod: 11 entradas vivas tras el
 *    logout). En un navegador compartido, con la red lenta (>10 s) o caída,
 *    la sesión siguiente -- de OTRO tenant -- podía recibir la respuesta
 *    cacheada de la anterior. Un SaaS autenticado no necesita leer la API
 *    sin red. El comentario viejo decía "Excluir rutas de API del caching" y
 *    el código hacía lo contrario.
 *
 * 2. La regla de imágenes (`asset-cache`) ahora EXCLUYE `/api/`. Antes su
 *    regex `^https://.*\.(png|...)$` también atrapaba una imagen servida por
 *    la API (documentos/fotos privadas de un tenant) y la guardaba 30 días
 *    en CacheFirst: el mismo defecto por la puerta de al lado.
 *
 * Las respuestas que YA quedaron en `api-cache` en los navegadores de los
 * clientes NO desaparecen solas al sacar la regla (`cleanupOutdatedCaches`
 * sólo limpia el precache, y la expiración de Workbox sólo corre cuando la
 * ruta se usa). Las borra `purgeLegacyApiCache` en registerServiceWorker.ts.
 */

export const LEGACY_API_CACHE_NAME = 'api-cache';

const IMAGE_URL = /^https:\/\/.*\.(?:png|jpg|jpeg|svg|gif|webp|ico)$/;

/** true si la URL pertenece a la API (cualquier host). Nunca va al cache del SW. */
export function isApiUrl(url: URL): boolean {
  return url.pathname.startsWith('/api/') || url.href.includes('/api/');
}

/** Imágenes públicas/estáticas cacheables. Excluye todo lo que venga de la API. */
export function isCacheableImage(url: URL): boolean {
  return !isApiUrl(url) && IMAGE_URL.test(url.href);
}

export const runtimeCaching = [
  {
    // ⚠️ AUTOCONTENIDA A PROPÓSITO. Workbox SERIALIZA esta función con
    // `.toString()` dentro de sw.js: no puede llamar a `isCacheableImage` ni
    // leer `IMAGE_URL` -- en el service worker esos nombres no existen y cada
    // fetch evaluado tiraría ReferenceError. Misma lógica que
    // `isCacheableImage`; el test la re-evalúa AISLADA para cazar eso.
    urlPattern: ({ url }: { url: URL }) =>
      !(url.pathname.startsWith('/api/') || url.href.includes('/api/')) &&
      /^https:\/\/.*\.(?:png|jpg|jpeg|svg|gif|webp|ico)$/.test(url.href),
    handler: 'CacheFirst' as const,
    options: {
      cacheName: 'asset-cache',
      expiration: {
        maxEntries: 60,
        maxAgeSeconds: 60 * 60 * 24 * 30,
      },
    },
  },
  // Sin regla para /api/ A PROPÓSITO (GH#1763): la API siempre va a la red.
];
